/* eslint-disable id-length */
import Icon from "~/components/shared/Icon";
import BaseNode from "~/components/Playground/Node/BaseNode";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { Node, NodeProps } from "@xyflow/react";
import { State } from "~/lib/pipeline/Step";
import { WaitStep } from "~/lib/pipeline";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

export const WaitIcon = (props: { passed: boolean }) => (
  <Icon
    className="h-5 w-5 stroke-2 text-current"
    icon={props.passed ? "heroicons/outline/chevron-right" : "heroicons/solid/pause"}
  />
);

type WaitStepNodeType = Node<WaitStep, "wait">;

/**
 * A node representing a wait step
 *
 * Wait step is slightly different from other steps in that it doesn't have a good state or outcome for
 * theming purposes. It's either waiting for dependencies or it's not. It can never be in a failed state.
 *
 * For theming, we determine the state of all upstream dependencies and if they all passed, we theme appropriately.
 * If any of them failed, we theme as if it's waiting for dependencies.
 */
export default function WaitStepNode(props: NodeProps<WaitStepNodeType>) {
  const { missingDependencies } = useStepDependencies(props.id);

  let passed = false;
  let { state, outcome } = props.data;

  if (state !== State.Ignored) {
    state = props.data.state;
    outcome = props.data.outcome;
    passed = state === State.Finished;
  }

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={outcome}
            missingDependencies={missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            Wait
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div data-testid="wait-step-node">
          <BaseNode
            hideStateIcon={true}
            {...props}
            data={{
              ...props.data,
              state,
              outcome,
            }}
            icon={<WaitIcon passed={passed} />}
          />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}
